import { useState, useEffect, useCallback } from "react";
import { FiSearch, FiArrowLeft, FiChevronRight, FiCheck, FiX, FiMapPin } from "react-icons/fi";
import { apiGet, apiPost, apiPut, apiDelete } from "../api";
import { PageHeader, Card, Pill, Btn, THead, TD, Icon } from "./KioskUI";

function stockStatus(p) {
  if (p.quantity === 0) return "out";
  if (p.quantity <= p.low_stock_threshold) return "low";
  return "ok";
}
const STATUS_LABEL = { ok: "In stock", low: "Low", out: "Out" };

/* ─── Add / Edit modal ─── */
function LocationModal({ existing, token, onClose, onSaved }) {
  const isEdit = !!existing;
  const [name, setName] = useState(existing?.name || "");
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState("");

  const canSave = name.trim().length > 0 && !saving;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSave) return;
    setSaving(true); setError("");
    try {
      const payload = { name: name.trim() };
      const res = isEdit
        ? await apiPut(`/api/inventory/locations/${existing.id}`, payload, token)
        : await apiPost("/api/inventory/locations", payload, token);
      if (res.ok) { onSaved(await res.json(), !isEdit); }
      else { const err = await res.json().catch(() => ({})); setError(err.error || "Failed to save."); }
    } catch { setError("Cannot connect to server."); }
    finally { setSaving(false); }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(19,24,32,.42)", backdropFilter: "blur(2px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60 }}>
      <form onClick={e => e.stopPropagation()} onSubmit={handleSubmit}
        style={{ background: "var(--surface)", borderRadius: "var(--r-xl)", boxShadow: "0 24px 70px rgba(19,24,32,.3)", width: 440, maxWidth: "94vw", padding: "24px 26px 22px", display: "flex", flexDirection: "column" }}>

        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 14, marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 13 }}>
            <div style={{ width: 44, height: 44, borderRadius: 13, background: "var(--blue-50)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <FiMapPin size={21} color="var(--primary)" />
            </div>
            <div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 9 }}>
                <span style={{ fontFamily: "var(--font-head)", fontSize: 20, fontWeight: 800, color: "var(--ink)", letterSpacing: "-.02em" }}>
                  {isEdit ? "Edit Location" : "Add Location"}
                </span>
                <span style={{ fontFamily: "var(--font-mm)", fontSize: 14, color: "var(--n-400)" }}>
                  {isEdit ? "တည်နေရာပြင်ဆင်" : "တည်နေရာအသစ်"}
                </span>
              </div>
              <div style={{ fontSize: 13, color: "var(--sub)", marginTop: 3 }}>A physical place stock is kept — a shop, a warehouse.</div>
            </div>
          </div>
          <button type="button" onClick={onClose} style={{ border: "1px solid var(--line)", background: "var(--surface)", borderRadius: "var(--r-sm)", padding: 7, cursor: "pointer", display: "flex", flexShrink: 0 }}>
            <FiX size={16} color="var(--sub)" />
          </button>
        </div>

        <MField label="Location name" mm="အမည်" required>
          <input autoFocus value={name} onChange={e => setName(e.target.value)}
            placeholder="e.g. Market" style={inpSt} />
        </MField>

        {error && (
          <div style={{ marginTop: 12, fontSize: 13, color: "var(--danger)", padding: "8px 12px", background: "var(--danger-bg)", borderRadius: "var(--r-sm)" }}>{error}</div>
        )}

        <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
          <Btn kind="ghost" onClick={onClose} style={{ flex: 1, justifyContent: "center" }}>Cancel</Btn>
          <button type="submit" disabled={!canSave}
            style={{ flex: 1, justifyContent: "center", display: "inline-flex", alignItems: "center", gap: 8, fontFamily: "var(--font-head)", fontWeight: 700, fontSize: 14.5, borderRadius: "var(--r-sm)", padding: "11px 18px", border: "none", cursor: canSave ? "pointer" : "not-allowed", background: canSave ? "var(--primary)" : "var(--n-200)", color: "#fff" }}>
            <FiCheck size={16} /> {saving ? "Saving…" : isEdit ? "Save Changes" : "Add Location"}
          </button>
        </div>
      </form>
    </div>
  );
}

function MField({ label, mm, required, style, children }) {
  return (
    <div style={style}>
      <label style={{ display: "flex", gap: 7, alignItems: "baseline", fontSize: 12.5, fontWeight: 700, color: "var(--sub)", letterSpacing: ".02em", marginBottom: 7 }}>
        {label}
        {required && <span style={{ color: "var(--danger)" }}>·</span>}
        {mm && <span style={{ fontFamily: "var(--font-mm)", fontWeight: 500, color: "var(--n-400)" }}>{mm}</span>}
      </label>
      <div style={{ display: "flex", alignItems: "center", border: "1px solid var(--line)", borderRadius: "var(--r-sm)", padding: "10px 13px", background: "var(--surface)" }}>
        {children}
      </div>
    </div>
  );
}
const inpSt = { border: "none", outline: "none", background: "transparent", width: "100%", fontFamily: "var(--font-body)", fontSize: 14.5, color: "var(--ink)" };

/* ─── Delete confirm ─── */
function DeleteConfirm({ location, onCancel, onConfirm, busy }) {
  return (
    <div onClick={onCancel} style={{ position: "fixed", inset: 0, background: "rgba(19,24,32,.42)", backdropFilter: "blur(2px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "var(--surface)", borderRadius: "var(--r-xl)", boxShadow: "0 24px 70px rgba(19,24,32,.3)", width: 380, padding: "26px 26px 22px" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 13, marginBottom: 14 }}>
          <div style={{ width: 42, height: 42, borderRadius: 12, background: "var(--danger-bg)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Icon name="trash" size={20} stroke="var(--danger)" />
          </div>
          <div>
            <div style={{ fontFamily: "var(--font-head)", fontSize: 17, fontWeight: 800, color: "var(--ink)" }}>Delete "{location.name}"?</div>
            <div style={{ fontSize: 13.5, color: "var(--sub)", marginTop: 4, lineHeight: 1.5 }}>
              This location will be removed. It must have zero stock and no transfer or sale history.
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
          <Btn kind="ghost" onClick={onCancel} style={{ flex: 1, justifyContent: "center" }}>Cancel</Btn>
          <button onClick={onConfirm} disabled={busy}
            style={{ flex: 1, justifyContent: "center", display: "inline-flex", alignItems: "center", gap: 8, fontFamily: "var(--font-head)", fontWeight: 700, fontSize: 14.5, borderRadius: "var(--r-sm)", padding: "11px 18px", border: "none", cursor: busy ? "not-allowed" : "pointer", background: busy ? "var(--n-200)" : "var(--danger)", color: "#fff" }}>
            <Icon name="trash" size={16} stroke="#fff" /> Delete
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Location detail ─── */
function LocationDetail({ locationId, token, onBack, onSaved, onDeleted }) {
  const [location, setLocation] = useState(null);
  const [products, setProducts] = useState([]);
  const [name, setName]         = useState("");
  const [saving, setSaving]     = useState(false);
  const [delConfirm, setDelConfirm] = useState(false);
  const [error, setError]       = useState("");

  useEffect(() => {
    Promise.all([
      apiGet(`/api/inventory/locations/${locationId}`, token).then(r => r.json()),
      apiGet(`/api/inventory/products/by-location?location_id=${locationId}`, token).then(r => r.json()),
    ]).then(([l, prods]) => {
      setLocation(l);
      setName(l.name);
      setProducts(Array.isArray(prods) ? prods : []);
    }).catch(console.error);
  }, [locationId, token]);

  const handleSave = async () => {
    setSaving(true); setError("");
    try {
      const res = await apiPut(`/api/inventory/locations/${locationId}`, { name }, token);
      if (res.ok) {
        const updated = await res.json();
        setLocation(updated);
        onSaved(updated);
      } else {
        const err = await res.json().catch(() => ({}));
        setError(err.error || "Failed to save.");
      }
    } catch { setError("Cannot connect to server."); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      const res = await apiDelete(`/api/inventory/locations/${locationId}`, token);
      if (res.ok) { onDeleted(locationId); }
      else { const err = await res.json().catch(() => ({})); setError(err.error || "Failed to delete."); setDelConfirm(false); }
    } catch { setError("Cannot connect to server."); setDelConfirm(false); }
    finally { setSaving(false); }
  };

  if (!location) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 240 }}>
      <span style={{ color: "var(--sub)", fontSize: 14 }}>Loading…</span>
    </div>
  );

  const totalStock = products.reduce((s, p) => s + (p.quantity || 0), 0);

  return (
    <div>
      {/* header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 22 }}>
        <button onClick={onBack} style={{ border: "1px solid var(--line)", background: "var(--surface)", borderRadius: "var(--r-sm)", padding: 8, cursor: "pointer", display: "flex" }}>
          <FiArrowLeft size={18} color="var(--sub)" />
        </button>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontFamily: "var(--font-head)", fontSize: 25, fontWeight: 800, letterSpacing: "-.025em", margin: 0, color: "var(--ink)" }}>{location.name}</h1>
          <div style={{ fontSize: 13.5, color: "var(--sub)", marginTop: 3 }}>#{location.id}{location.is_default ? " · Default restock destination" : ""}</div>
        </div>
        {location.is_default && <Pill tone="ok">DEFAULT</Pill>}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1.55fr 1fr", gap: 16, alignItems: "start" }}>
        {/* left */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* editable details */}
          <Card style={{ padding: 24 }}>
            <div style={{ fontFamily: "var(--font-head)", fontSize: 15, fontWeight: 700, color: "var(--ink)", marginBottom: 18 }}>Location details</div>
            <MField label="Location name" mm="အမည်">
              <input value={name} onChange={e => setName(e.target.value)} style={inpSt} />
            </MField>
            {error && (
              <div style={{ marginTop: 12, fontSize: 13, color: "var(--danger)", padding: "8px 12px", background: "var(--danger-bg)", borderRadius: "var(--r-sm)" }}>{error}</div>
            )}
          </Card>

          {/* stock at this location */}
          <Card style={{ overflow: "hidden" }}>
            <div style={{ padding: "15px 20px", borderBottom: "1px solid var(--hair)", display: "flex", alignItems: "baseline", gap: 10 }}>
              <div style={{ fontFamily: "var(--font-head)", fontSize: 16, fontWeight: 700, color: "var(--ink)" }}>Stock at this location</div>
              <span style={{ fontFamily: "var(--font-mm)", fontSize: 13.5, color: "var(--n-400)" }}>လက်ကျန်ပစ္စည်း</span>
              <span style={{ marginLeft: "auto", fontSize: 12.5, color: "var(--sub)" }}>{products.length} items</span>
            </div>
            {products.length === 0 ? (
              <div style={{ padding: "30px 20px", textAlign: "center", color: "var(--n-400)", fontSize: 13.5 }}>
                No products on record.
              </div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <THead cols={[{ t: "Product" }, { t: "SKU" }, { t: "Stock", r: 1 }, { t: "Status", r: 1 }]} />
                <tbody>
                  {products.map(p => {
                    const st = stockStatus(p);
                    return (
                      <tr key={p.id}
                        onMouseEnter={e => e.currentTarget.style.background = "var(--faint)"}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                      >
                        <TD b>{p.name}</TD>
                        <TD mut>{p.sku || "—"}</TD>
                        <TD r>{p.quantity}</TD>
                        <td style={{ padding: "14px 16px", borderTop: "1px solid var(--hair)", textAlign: "right" }}>
                          <Pill tone={st}>{STATUS_LABEL[st]}</Pill>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </Card>
        </div>

        {/* right */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {/* summary */}
          <Card style={{ padding: 24 }}>
            <div style={{ fontFamily: "var(--font-head)", fontSize: 15, fontWeight: 700, color: "var(--ink)", marginBottom: 6 }}>Summary</div>
            <div style={{ fontSize: 12.5, color: "var(--sub)", marginBottom: 14 }}>{products.length} product{products.length !== 1 ? "s" : ""} on record</div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: 12, marginTop: 4, borderTop: "1px solid var(--hair)" }}>
              <span style={{ fontSize: 13.5, fontWeight: 700, color: "var(--ink)" }}>Total units here</span>
              <span style={{ fontFamily: "var(--font-head)", fontSize: 17, fontWeight: 800, color: "var(--ink)", fontVariantNumeric: "tabular-nums" }}>
                {totalStock}
              </span>
            </div>
          </Card>

          {/* actions */}
          <div style={{ display: "flex", gap: 11 }}>
            <Btn kind="ghost" onClick={onBack} style={{ flex: 1, justifyContent: "center" }}>Cancel</Btn>
            <Btn kind="pri" icon="check" onClick={handleSave} disabled={saving} style={{ flex: 1, justifyContent: "center" }}>
              {saving ? "Saving…" : "Save"}
            </Btn>
          </div>
          {location.is_default ? (
            <div style={{ fontSize: 12, color: "var(--n-400)", padding: "9px 12px", background: "var(--faint)", borderRadius: "var(--r-sm)", lineHeight: 1.5 }}>
              This is the default restock destination — it can't be deleted.
            </div>
          ) : (
            <Btn kind="danger" icon="trash" onClick={() => setDelConfirm(true)} style={{ justifyContent: "center" }}>
              Delete location
            </Btn>
          )}
        </div>
      </div>

      {delConfirm && (
        <DeleteConfirm location={location} busy={saving} onCancel={() => setDelConfirm(false)} onConfirm={handleDelete} />
      )}
    </div>
  );
}

/* ─── Location list ─── */
export default function Locations({ token, go, onChanged }) {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [q, setQ]                 = useState("");
  const [adding, setAdding]       = useState(false);
  const [view, setView]           = useState("list");
  const [selectedId, setSelectedId] = useState(null);

  const fetchLocations = useCallback(async () => {
    try {
      const r = await apiGet("/api/inventory/locations", token);
      const d = await r.json();
      setLocations(Array.isArray(d) ? d : []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchLocations(); }, [fetchLocations]);

  const openDetail = (id) => { setSelectedId(id); setView("detail"); };
  const backToList = () => { setView("list"); setSelectedId(null); };

  const handleAdded   = (l) => { setLocations(prev => [...prev, l]); setAdding(false); openDetail(l.id); onChanged?.(); };
  const handleSaved   = (l) => { setLocations(prev => prev.map(x => x.id === l.id ? l : x)); onChanged?.(); };
  const handleDeleted = (id) => { setLocations(prev => prev.filter(x => x.id !== id)); backToList(); onChanged?.(); };

  if (view === "detail") {
    return (
      <LocationDetail
        locationId={selectedId}
        token={token}
        go={go}
        onBack={backToList}
        onSaved={handleSaved}
        onDeleted={handleDeleted}
      />
    );
  }

  const qq   = q.trim().toLowerCase();
  const list = locations.filter(l => !qq || l.name.toLowerCase().includes(qq));

  return (
    <div>
      <PageHeader title="Locations" mm="တည်နေရာများ" desc={`${locations.length} location${locations.length !== 1 ? "s" : ""}`}>
        <div style={{ display: "flex", alignItems: "center", gap: 9, background: "var(--surface)", border: "1px solid var(--line)", borderRadius: "var(--r-sm)", padding: "9px 13px", width: 280 }}>
          <FiSearch size={16} color="var(--n-400)" style={{ flexShrink: 0 }} />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search locations…"
            style={{ border: "none", outline: "none", background: "transparent", fontFamily: "var(--font-body)", fontSize: 14, color: "var(--ink)", width: "100%" }} />
        </div>
        <Btn kind="pri" icon="plus" onClick={() => setAdding(true)}>Add Location</Btn>
      </PageHeader>

      {loading ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200 }}>
          <span style={{ color: "var(--sub)", fontSize: 14 }}>Loading…</span>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 16 }}>
          {list.length === 0 && (
            <div style={{ gridColumn: "1 / -1", textAlign: "center", color: "var(--n-400)", fontSize: 14, padding: "48px 0" }}>
              {qq ? `No locations match "${q}".` : "No locations yet."}
            </div>
          )}
          {list.map(l => (
            <LocationCard key={l.id} location={l} onClick={() => openDetail(l.id)} />
          ))}
        </div>
      )}

      {adding && (
        <LocationModal token={token} onClose={() => setAdding(false)} onSaved={handleAdded} />
      )}
    </div>
  );
}

function LocationCard({ location, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <Card
      style={{ padding: "20px 22px", cursor: "pointer", borderColor: hover ? "var(--blue-100)" : "var(--line)", background: hover ? "var(--faint)" : "var(--surface)", transition: "border-color .12s, background .12s" }}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
        <div style={{ width: 46, height: 46, borderRadius: 13, background: "var(--n-100)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <FiMapPin size={21} color="var(--n-600)" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ fontFamily: "var(--font-head)", fontSize: 16.5, fontWeight: 700, color: "var(--ink)" }}>{location.name}</div>
            {location.is_default && <Pill tone="ok">DEFAULT</Pill>}
          </div>
          <div style={{ fontSize: 13, color: "var(--sub)", marginTop: 3 }}>{location.is_default ? "Restocks land here" : "Working location"}</div>
        </div>
        <FiChevronRight size={18} color="var(--n-300)" />
      </div>
    </Card>
  );
}
